import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/fieldops/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Trash2, Search, Loader2 } from "lucide-react";

const PAGE = 50;
const inputCls = "h-8 text-sm";

/** The coded-item master: every part number the workbook carries, searchable, admin CRUD. */
function CodedItemsList() {
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ total: 0, rows: [] });
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async (term, off) => {
    setLoading(true);
    try {
      const r = await api.get("/item-codes", { params: { search: term, limit: PAGE, offset: off } });
      setData(r.data);
    } catch { toast.error("Could not load the item codes"); }
    setLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setOffset(0); load(search, 0); }, 300);
    return () => clearTimeout(t);
  }, [search, load]);

  const save = async () => {
    const body = {
      part_number: editing.part_number || "", acronym: editing.acronym || "",
      size: editing.size || "", color: editing.color || "",
      full_description: editing.full_description || "",
      acronym_means: editing.acronym_means || "", manufacturer: editing.manufacturer || "",
      cost_code: editing.cost_code || "", active: editing.active !== false,
    };
    try {
      if (editing.id) await api.patch(`/item-codes/${editing.id}`, body);
      else await api.post("/item-codes", body);
      toast.success("Saved");
      setEditing(null);
      load(search, offset);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const del = async (row) => {
    toast(`Delete ${row.short_form || row.acronym}?`, {
      duration: 8000,
      action: {
        label: "Delete",
        onClick: async () => {
          try { await api.delete(`/item-codes/${row.id}`); load(search, offset); }
          catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
        },
      },
      cancel: { label: "Cancel" },
      classNames: { actionButton: "!bg-red-600 !text-white hover:!bg-red-700" },
    });
  };

  const page = (next) => { setOffset(next); load(search, next); };

  return (
    <div className="space-y-3" data-testid="frs-codes-items">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-gray-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search..."
                 className={`${inputCls} pl-7`} data-testid="frs-codes-search" />
        </div>
        <span className="text-xs text-gray-500" data-testid="frs-codes-total">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `${data.total} coded items`}
        </span>
        <Button size="sm" className="ml-auto bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setEditing({ acronym: "", part_number: "" })}
                data-testid="frs-code-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add item
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-auto max-h-[60vh] pb-16">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
            <tr className="text-xs text-gray-500 uppercase tracking-wider">
              <th className="text-left px-3 py-2 font-medium w-44">Short form</th>
              <th className="text-left px-3 py-2 font-medium w-20">Acronym</th>
              <th className="text-left px-3 py-2 font-medium">Full description</th>
              <th className="text-left px-3 py-2 font-medium w-40">Manufacturer</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50"
                  data-testid={`frs-code-row-${r.id}`}>
                <td className="px-3 py-1.5 font-mono text-xs font-semibold">{r.short_form}</td>
                <td className="px-3 py-1.5 text-xs text-gray-600">{r.acronym}</td>
                <td className="px-3 py-1.5 text-gray-700 truncate max-w-[420px]"
                    title={r.full_description}>{r.full_description}</td>
                <td className="px-3 py-1.5 text-xs text-gray-500">{r.manufacturer}</td>
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => setEditing({ ...r })}
                          data-testid={`frs-code-edit-${r.id}`}>Edit</Button>
                  <Button variant="ghost" size="sm" onClick={() => del(r)}>
                    <Trash2 className="w-3.5 h-3.5 text-gray-500" />
                  </Button>
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && !loading && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                Nothing matches that search.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{data.total === 0 ? "—" : `${offset + 1}–${Math.min(offset + PAGE, data.total)} of ${data.total}`}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={offset === 0}
                  onClick={() => page(Math.max(0, offset - PAGE))}
                  data-testid="frs-codes-prev">Previous</Button>
          <Button variant="outline" size="sm" disabled={offset + PAGE >= data.total}
                  onClick={() => page(offset + PAGE)} data-testid="frs-codes-next">Next</Button>
        </div>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
             onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full"
               onClick={(e) => e.stopPropagation()} data-testid="frs-code-dialog">
            <header className="px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold">{editing.id ? "Edit coded item" : "Add coded item"}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Shown on the FRS as <span className="font-mono">part# ACRONYM-SIZE COLOR</span>.
              </p>
            </header>
            <div className="p-4 grid grid-cols-2 gap-3">
              {[["part_number", "Part number"], ["acronym", "Acronym *"], ["size", "Size"],
                ["color", "Colour / finish code"], ["manufacturer", "Manufacturer"],
                ["acronym_means", "Acronym means"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs text-gray-600">{label}</label>
                  <Input value={editing[k] || ""} data-testid={`frs-code-field-${k}`}
                         onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs text-gray-600">Full description</label>
                <Input value={editing.full_description || ""} data-testid="frs-code-field-desc"
                       onChange={(e) => setEditing({ ...editing, full_description: e.target.value })} />
              </div>
            </div>
            <footer className="px-4 py-3 border-t border-gray-200 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={save}
                      data-testid="frs-code-save">Save</Button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

/** The acronym legend — what each code means. */
function AcronymsList() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ acronym: "", means: "" });
  const load = () => api.get("/item-acronyms").then((r) => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.acronym.trim()) return toast.error("An acronym is required");
    try {
      await api.post("/item-acronyms", { acronym: form.acronym.trim(), means: form.means.trim() });
      setForm({ acronym: "", means: "" }); load(); toast.success("Acronym added");
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const patch = async (row, means) => {
    try { await api.patch(`/item-acronyms/${row.id}`, { acronym: row.acronym, means }); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); load(); }
  };
  const del = async (row) => {
    try { await api.delete(`/item-acronyms/${row.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden"
         data-testid="frs-codes-acronyms">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Acronyms ({rows.length})</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          The product code and what it means. Import matching reads these, so a new acronym works
          on the next upload.
        </p>
        <div className="flex flex-wrap items-end gap-2 mt-3">
          <Input value={form.acronym} className={`${inputCls} w-28 font-mono uppercase`}
                 placeholder="TB" data-testid="acronym-code-input"
                 onChange={(e) => setForm({ ...form, acronym: e.target.value.toUpperCase() })} />
          <Input value={form.means} className={`${inputCls} w-72`} placeholder="Towel Bar"
                 data-testid="acronym-means-input"
                 onChange={(e) => setForm({ ...form, means: e.target.value })} />
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={add}
                  data-testid="acronym-add-btn">Add</Button>
        </div>
      </div>
      <div className="overflow-auto max-h-[55vh] pb-16">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr className="text-[10px] uppercase tracking-wider text-gray-500">
              <th className="text-left px-4 py-2 font-medium w-28">Acronym</th>
              <th className="text-left px-3 py-2 font-medium">Means</th>
              <th className="text-left px-3 py-2 font-medium w-20">Rows</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`acronym-row-${r.id}`}>
                <td className="px-4 py-1.5 font-mono text-xs font-semibold">{r.acronym}</td>
                <td className="px-3 py-1.5">
                  <input defaultValue={r.means || ""}
                         onBlur={(e) => e.target.value !== (r.means || "") && patch(r, e.target.value)}
                         className="w-full bg-transparent text-gray-800 outline-none"
                         data-testid={`acronym-means-${r.id}`} />
                </td>
                <td className="px-3 py-1.5 text-xs text-gray-400">{r.rows || "—"}</td>
                <td className="px-3 py-1.5 text-right">
                  <button onClick={() => del(r)} className="text-xs text-red-600 hover:underline"
                          data-testid={`acronym-delete-${r.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Finish / colour codes, including the pairs that mean the same finish (BN/BNX). */
function FinishesList() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ code: "", finish: "", alt_code: "" });
  const load = () => api.get("/item-finishes").then((r) => setRows(r.data.rows || [])).catch(() => {});
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.code.trim()) return toast.error("A code is required");
    try {
      await api.post("/item-finishes", form);
      setForm({ code: "", finish: "", alt_code: "" }); load(); toast.success("Finish code added");
    } catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  const del = async (row) => {
    try { await api.delete(`/item-finishes/${row.id}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden"
         data-testid="frs-codes-finishes">
      <div className="px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Finish codes ({rows.length})</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Written the way the manufacturer writes them, so the same finish can carry two codes
          (BN / BNX, BL / MBLK).
        </p>
        <div className="flex flex-wrap items-end gap-2 mt-3">
          <Input value={form.code} className={`${inputCls} w-24 font-mono uppercase`} placeholder="CH"
                 data-testid="finish-code-input"
                 onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
          <Input value={form.finish} className={`${inputCls} w-56`} placeholder="Chrome"
                 data-testid="finish-name-input"
                 onChange={(e) => setForm({ ...form, finish: e.target.value })} />
          <Input value={form.alt_code} className={`${inputCls} w-28 font-mono uppercase`}
                 placeholder="alt code" data-testid="finish-alt-input"
                 onChange={(e) => setForm({ ...form, alt_code: e.target.value.toUpperCase() })} />
          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={add}
                  data-testid="finish-add-btn">Add</Button>
        </div>
      </div>
      <div className="overflow-auto max-h-[55vh] pb-16">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0">
            <tr className="text-[10px] uppercase tracking-wider text-gray-500">
              <th className="text-left px-4 py-2 font-medium w-24">Code</th>
              <th className="text-left px-3 py-2 font-medium">Finish</th>
              <th className="text-left px-3 py-2 font-medium w-32">Same finish, other code</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100" data-testid={`finish-row-${r.id}`}>
                <td className="px-4 py-1.5 font-mono text-xs font-semibold">{r.code}</td>
                <td className="px-3 py-1.5 text-gray-800">{r.finish}</td>
                <td className="px-3 py-1.5 font-mono text-xs text-gray-500">{r.alt_code || "—"}</td>
                <td className="px-3 py-1.5 text-right">
                  <button onClick={() => del(r)} className="text-xs text-red-600 hover:underline"
                          data-testid={`finish-delete-${r.id}`}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Settings > Field Codes > FRS Codes. */
export default function FrsCodesSection() {
  return (
    <div className="space-y-3" data-testid="frs-codes-section">
      <p className="text-xs text-gray-500">
        The FRS shows a short coded description — <span className="font-mono">part# ACRONYM-SIZE
        COLOR</span> — instead of the long product name, so a room fits on a phone. The long
        description is kept on every row.
      </p>
      <Tabs defaultValue="items" className="w-full">
        <TabsList className="bg-white border border-slate-200 rounded-md h-auto min-h-9 px-1">
          <TabsTrigger value="items" data-testid="frs-codes-tab-items">Coded items</TabsTrigger>
          <TabsTrigger value="acronyms" data-testid="frs-codes-tab-acronyms">Acronyms</TabsTrigger>
          <TabsTrigger value="finishes" data-testid="frs-codes-tab-finishes">Finish codes</TabsTrigger>
        </TabsList>
        <div className="mt-3">
          <TabsContent value="items"><CodedItemsList /></TabsContent>
          <TabsContent value="acronyms"><AcronymsList /></TabsContent>
          <TabsContent value="finishes"><FinishesList /></TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
